import { DashboardDateSelectionContext } from "./DashboardDateSelectionContext";
import type { DateRangePreset } from "./dateSelection";
import { processDetailHref } from "../processes/processDetailHref";
import React, { useContext, useId, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { ProcessOverview } from "./processOverview";
import { formatDuration } from "../../utils";
import { appPrimaryColor, getAllocationColor, INSIGNIFICANT_COLOR } from "../analytics/palette";
import { appIconUri } from "../processes/appIcon";

type AllocationRow = {
  name: string;
  seconds: number;
  color: string;
  slug?: string;
  icon?: string | null;
};

function AllocationCard({
  title,
  totalLabel,
  emptyLabel,
  expandLabel,
  rows,
  total,
  userId,
  from,
  to,
  preset,
}: {
  title: string;
  totalLabel: string;
  emptyLabel: string;
  expandLabel: string;
  rows: AllocationRow[];
  total: number;
  userId: string;
  from: string;
  to: string;
  preset?: DateRangePreset;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const titleId = useId();
  const listId = useId();
  const remaining = rows.slice(5);
  const visible = expanded ? rows : rows.slice(0, 5);
  const other = !expanded && remaining.length > 0;
  const displayed: AllocationRow[] = other
    ? [
        ...visible,
        {
          name: t("activity.other"),
          seconds: remaining.reduce((sum, row) => sum + row.seconds, 0),
          color: INSIGNIFICANT_COLOR,
        },
      ]
    : visible;
  return (
    <section
      aria-labelledby={titleId}
      className="min-w-0 rounded-xl border border-outline bg-muted-surface/20 p-4"
    >
      <h3 id={titleId} className="font-semibold">
        {title}
      </h3>
      <p className="mt-1 text-xs text-text-dim">{totalLabel}</p>
      <ul id={listId} className="mt-3 space-y-1">
        {displayed.map((row, index) => {
          const isOther = other && index === visible.length;
          const share = total > 0 ? row.seconds / total : 0;
          const content = (
            <>
              <span className="flex items-start gap-2">
                {row.icon ? (
                  <img src={row.icon} alt="" className="mt-0.5 h-4 w-4 shrink-0 object-contain" />
                ) : (
                  <span
                    aria-hidden="true"
                    className="mt-1.5 h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: row.color }}
                  />
                )}
                <span className="min-w-0 flex-1 break-words text-sm leading-5">{row.name}</span>
                <span className="shrink-0 text-right text-xs font-medium leading-5 tabular-nums text-text">
                  {formatDuration(row.seconds)}{" "}
                  <span className="font-normal text-text-mid">
                    ({share > 0 && share < 0.01 ? "<1" : Math.round(share * 100)}%)
                  </span>
                </span>
              </span>
              <span
                aria-hidden="true"
                className="mt-2 block h-1.5 overflow-hidden rounded-full bg-muted-surface"
              >
                <span
                  className="block h-full rounded-full"
                  style={{ width: `${Math.min(100, share * 100)}%`, backgroundColor: row.color }}
                />
              </span>
            </>
          );
          const className = "block w-full rounded-lg p-2 text-left focus-visible:outline-info";
          return (
            <li key={row.slug ?? `${index}:${row.name}`}>
              {row.slug ? (
                <Link
                  to={processDetailHref(row.slug, [userId], { from, to, preset })}
                  className={`${className} hover:bg-info/5 hover:text-info`}
                >
                  {content}
                </Link>
              ) : isOther ? (
                <button
                  type="button"
                  aria-expanded={false}
                  aria-controls={listId}
                  onClick={() => setExpanded(true)}
                  className={`${className} hover:bg-muted-surface`}
                >
                  {content}
                </button>
              ) : (
                <div className={className}>{content}</div>
              )}
            </li>
          );
        })}
      </ul>
      {remaining.length > 0 && (
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded(value => !value)}
          className="inline-flex items-center gap-2 pt-5 text-sm font-medium text-info hover:underline focus-visible:outline-info"
        >
          {expanded ? t("activity.show_less") : expandLabel}
          <ChevronDown className={`h-4 w-4 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      )}
      {!rows.length && <p className="py-8 text-center text-sm text-text-dim">{emptyLabel}</p>}
    </section>
  );
}

export default function ActivityAllocation({
  metrics,
  userId,
  from,
  to,
  preset,
}: {
  metrics: ProcessOverview;
  userId: string;
  from: string;
  to: string;
  preset?: DateRangePreset;
}) {
  const { t } = useTranslation();
  const { selection: dates } = useContext(DashboardDateSelectionContext);
  const scope = { userId, from, to, preset: preset ?? dates?.preset };
  return (
    <div className="grid items-start gap-3 @[680px]:grid-cols-2">
      <AllocationCard
        {...scope}
        title={t("activity.time_by_process")}
        totalLabel={t("activity.process_time_total", { time: formatDuration(metrics.seconds) })}
        emptyLabel={t("activity.empty_processes")}
        expandLabel={t("dashboard.show_all_processes", { count: metrics.processes.length })}
        total={metrics.seconds}
        rows={metrics.processes.map((row, index) => ({ ...row, color: getAllocationColor(index) }))}
      />
      <AllocationCard
        {...scope}
        title={t("activity.time_by_app")}
        totalLabel={t("activity.app_time_total", { time: formatDuration(metrics.appSeconds) })}
        emptyLabel={t("activity.empty_apps")}
        expandLabel={t("dashboard.show_all_apps", { count: metrics.apps.length })}
        total={metrics.appSeconds}
        rows={metrics.apps.map(row => ({
          ...row,
          color: appPrimaryColor(row.name),
          icon: appIconUri(row.name),
        }))}
      />
    </div>
  );
}
